import {
  CLUB_IMPORT_NUDGE_ACTION_EVENT,
  CLUB_IMPORT_NUDGE_EVENT,
  type ClubImportNudgeParams,
  clubImportNudgeProps,
} from "@repo/shared/analytics";

import { mixpanelTrack } from "./mixpanelTrack";

// Club import, end to end:
//
//   club_import_cta            every "Import Club" CTA in the app, wherever it sits
//     -> club_import_tutorial_viewed
//       -> club_import_step_completed (one per checklist step)
//         -> import_club       the EA web app actually opened
//           -> club_import_done  "Done", and what came back
//
// The first two rungs and the last carry different sections on purpose:
// `club_import_cta` and `import_club` are stamped with the surface the user
// came *from*, everything in between with the tutorial screen they are on.

const SECTION = "Club Import";

/**
 * Where an import was chosen. Same shape and vocabulary as the signup context's
 * `section` / `subsection` (see components/features/ClubImport/clubImportPrompt),
 * so a CTA's clicks, its imports and its signups all line up.
 */
export type ClubImportOriginProps = {
  section: string;
  subsection?: string;
};

export type ClubImportSubsection =
  /** The desktop tutorial: checklist and the import buttons on one page. */
  | "Desktop"
  /** The touch tutorial's checklist. */
  | "Mobile"
  /** The touch flow's second screen, which carries Start Import and Done. */
  | "Mobile Guide"
  /** The full-screen importing overlay, which carries its own Done. */
  | "Overlay";

/**
 * Checklist steps, by name rather than by number — the same step is 3rd on
 * desktop and 4th on mobile, so the number only means something beside its
 * subsection.
 */
export type ClubImportStepName = "browser" | "login" | "ios-app" | "extension";

/**
 * An import CTA was clicked, anywhere in the app.
 *
 * Intent, not outcome: most CTAs route to the tutorial rather than importing
 * anything, and the gap between this and `import_club` is the whole point —
 * it is where people are lost between wanting a club and having one.
 */
/**
 * A club-import nudge was shown, and how it was left.
 *
 * Its own pair of events rather than the credit dialogs': a nudge is a pitch,
 * not a refusal, and most of the people who see one have never been shown a
 * credit. See the note in @repo/shared/analytics.
 */
export const trackClubImportNudge = (params: ClubImportNudgeParams) =>
  mixpanelTrack(CLUB_IMPORT_NUDGE_EVENT, clubImportNudgeProps(params));

export const trackClubImportNudgeAction = (
  params: ClubImportNudgeParams & { value: "positive" | "negative" },
) =>
  mixpanelTrack(CLUB_IMPORT_NUDGE_ACTION_EVENT, {
    ...clubImportNudgeProps(params),
    value: params.value,
  });

export const trackClubImportCta = (origin: ClubImportOriginProps) =>
  mixpanelTrack("club_import_cta", {
    section: origin.section,
    subsection: origin.subsection,
    action_type: "core",
  });

/**
 * An import actually started — the EA web app opened.
 *
 * The event three surfaces already fired by hand; now fired once, in
 * useLaunchClubImport, which every launch in the app goes through. Its section
 * is the CTA's origin, so it stays comparable with what SBC / My Club /
 * Evolutions were sending, and the tutorial's own Start Import is included for
 * the first time (as section "Club Import").
 */
export const trackImportClub = (origin: ClubImportOriginProps) =>
  mixpanelTrack("import_club", {
    section: origin.section,
    subsection: origin.subsection,
    action_type: "core",
  });

export const trackClubImportTutorialViewed = (
  subsection: ClubImportSubsection,
) =>
  mixpanelTrack("club_import_tutorial_viewed", {
    section: SECTION,
    subsection,
    action_type: "core",
  });

/**
 * A checklist step went green. Fired once per step per visit, on the first
 * render that reports it complete — steps already done on arrival (a Chrome
 * user's "use Chrome") still count, or the funnel would show every desktop user
 * failing step 1.
 */
export const trackClubImportStepCompleted = (params: {
  subsection: ClubImportSubsection;
  value: ClubImportStepName;
}) =>
  mixpanelTrack("club_import_step_completed", {
    section: SECTION,
    subsection: params.subsection,
    value: params.value,
    action_type: "core",
  });

/**
 * "Done" — fired on the answer rather than on the click, because pressing Done
 * is not importing. Two of the three outcomes are failures, and without them a
 * user who presses Done four times and never gets a club reads as a conversion.
 */
export const trackClubImportDone = (params: {
  subsection: ClubImportSubsection;
  value: "success" | "blocked" | "error";
}) =>
  mixpanelTrack("club_import_done", {
    section: SECTION,
    subsection: params.subsection,
    value: params.value,
    action_type: "core",
  });
